import React from 'react';
import { BookOpen, Tag, FileText, Link as LinkIcon, Calendar } from 'lucide-react';

const TerminoDetail = ({ termino }) => {
  const DetailSection = ({ icon: Icon, title, content, emptyText = 'No disponible' }) => {
    if (!content || !content.trim()) {
      return (
        <div className="py-4 border-b border-gray-200 last:border-0">
          <div className="flex items-start">
            <Icon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-gray-500 mb-1">{title}</h4>
              <p className="text-sm text-gray-400 italic">{emptyText}</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="py-4 border-b border-gray-200 last:border-0">
        <div className="flex items-start">
          <Icon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-500 mb-2">{title}</h4>
            <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">
              {content}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryColor = (categoria) => {
    const colors = {
      'Ética General': 'bg-blue-100 text-blue-800',
      'Ética Profesional': 'bg-purple-100 text-purple-800',
      'Bioética': 'bg-green-100 text-green-800',
      'Ética Digital': 'bg-indigo-100 text-indigo-800',
      'Derechos Humanos': 'bg-red-100 text-red-800',
      'Responsabilidad Social': 'bg-yellow-100 text-yellow-800',
      'Integridad': 'bg-pink-100 text-pink-800',
      'Transparencia': 'bg-cyan-100 text-cyan-800',
      'Compliance': 'bg-orange-100 text-orange-800',
      'Otros': 'bg-gray-100 text-gray-800',
    };
    return colors[categoria] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      {/* Header con término y categoría */}
      <div className="border-b border-gray-200 pb-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-primary-600 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {termino.termino}
              </h3>
              <p className="text-sm text-gray-500">ID: {termino.id_termino}</p>
            </div>
          </div>
        </div>
        
        <div className="mt-3">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(
              termino.categoria
            )}`}
          >
            <Tag className="h-4 w-4 mr-1" />
            {termino.categoria}
          </span>
        </div>
      </div>

      {/* Definición */}
      <div className="bg-primary-50 rounded-lg p-4 border-l-4 border-primary-500">
        <h4 className="text-sm font-semibold text-primary-900 mb-2 uppercase tracking-wide">
          Definición
        </h4>
        <p className="text-gray-900 leading-relaxed">
          {termino.definicion}
        </p>
      </div>

      {/* Ejemplo y Referencias */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="space-y-0 divide-y divide-gray-200">
          <DetailSection
            icon={FileText}
            title="Ejemplo de Uso"
            content={termino.ejemplo}
            emptyText="No se proporcionó un ejemplo"
          />
          <DetailSection
            icon={LinkIcon}
            title="Referencias"
            content={termino.referencias}
            emptyText="No se proporcionaron referencias"
          />
        </div>
      </div>

      {/* Información de auditoría */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">
          Información del Registro
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <p className="text-xs text-gray-500">Fecha de creación</p>
              <p className="text-sm text-gray-900 font-medium">
                {formatDate(termino.fecha_creacion)}
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-gray-400 mr-2" />
            <div>
              <p className="text-xs text-gray-500">Última actualización</p>
              <p className="text-sm text-gray-900 font-medium">
                {formatDate(termino.fecha_actualizacion)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Nota informativa */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <BookOpen className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-blue-900 mb-1">
              Glosario de Ética
            </h4>
            <p className="text-sm text-blue-800">
              Este término forma parte del glosario de términos éticos del sistema MED,
              diseñado para promover una comprensión común de conceptos fundamentales
              en ética profesional y organizacional.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminoDetail;
